import { TwitterTweetEmbed } from "react-twitter-embed";
import "./LinkCard.scoped.scss";
import "./LinkCard.scss";

interface Props {
  url: string;
  width: number;
  height: number;
}

const youtubeVideoUrlRegex = new RegExp(
  "^https://(.*youtube.com/watch|youtu.be/).*"
);
const youtubePlaylistUrlRegex = new RegExp(
  "^https://(.*youtube.com/playlist).*"
);
const twitterUrlRegex = new RegExp("^https://(twitter.com|t.co)/.*");
const nicoliveUrlRegex = new RegExp(
  "^https://live.nicovideo.jp/watch/(?<liveid>lv[0-9]+).*"
);

export default function QuizQuestion({ url, width, height }: Props) {
  const infer_oembed_provider = (url: string) => {
    if (youtubeVideoUrlRegex.test(url)) {
      return "youtube-video";
    }
    if (youtubePlaylistUrlRegex.test(url)) {
      return "youtube-playlist";
    }
    if (twitterUrlRegex.test(url)) {
      return "twitter";
    }
    if (nicoliveUrlRegex.test(url)) {
      return "nicolive";
    }
    if (url.startsWith("!")) {
      return "linkcard";
    }
    return null;
  };

  const get_youtube_video_embed_url = (url: string) => {
    const parser = new URL(url);
    let videoid: string | null;
    let start: string | null;
    if (parser.host === "www.youtube.com") {
      videoid = parser.searchParams.get("v");
      start = parser.searchParams.get("t");
    } else if (parser.host === "youtu.be") {
      videoid = parser.pathname.split("/").pop() || null;
      start = parser.searchParams.get("t");
    } else {
      throw new Error(`invalid url: ${url}`);
    }
    let ret = `https://www.youtube.com/embed/${videoid}`;
    if (start) {
      ret += `?start=${start.replace("s", "")}`;
    }
    return ret;
  };

  const get_youtube_playlist_embed_url = (url: string) => {
    const parser = new URL(url);
    const listid = parser.searchParams.get("list");
    return `https://www.youtube.com/embed/videoseriese?list=${listid}`;
  };

  const get_tweet_id = (url: string) => {
    const parser = new URL(url);
    const tweetId = parser.pathname.split("/").pop();
    if (!tweetId) {
      throw new Error(`invalid url: ${url}`);
    }
    return tweetId;
  };

  const get_linkcard_title_and_url = (value: string) => {
    if (!value.startsWith("!")) throw new Error("invalid linkcard url");
    value = value.slice(1);
    const [title, url] = value.split("<>");
    return [title, url];
  };

  const get_nicolive_embed_url = (url: string) => {
    const match = nicoliveUrlRegex.exec(url);
    if (!match || !match.groups)
      throw new Error(`invalid nicolive url: ${url}`);
    return `https://live.nicovideo.jp/embed/${match.groups.liveid}`;
  };

  return (
    <div className="w-full overflow-hidden rounded-xl">
      {(() => {
        switch (infer_oembed_provider(url)) {
          case "youtube-video":
            return (
              <div className="youtube-wrapper">
                <iframe
                  src={get_youtube_video_embed_url(url)}
                  title={url}
                  frameBorder="0"
                  width={width}
                  height={height}
                ></iframe>
              </div>
            );
          case "youtube-playlist":
            return (
              <div className="youtube-wrapper">
                <iframe
                  src={get_youtube_playlist_embed_url(url)}
                  title={url}
                  frameBorder="0"
                ></iframe>
              </div>
            );
          case "twitter":
            return (
              <div className="twitter-wrapper">
                <TwitterTweetEmbed
                  tweetId={get_tweet_id(url)}
                  options={{ style: "" }}
                />
              </div>
            );

          case "nicolive":
            return (
              <div className="nicolive-wrapper">
                <iframe
                  src={get_nicolive_embed_url(url)}
                  title={url}
                  scrolling="no"
                  frameBorder="0"
                ></iframe>
              </div>
            );
          case "linkcard":
            return (
              <div className="relative w-full p-3 overflow-hidden bg-white cursor-pointer select-none rounded-md">
                <div className="text-gray-700">
                  {get_linkcard_title_and_url(url)[0]}
                </div>
                <div className="text-xs text-blue-500">
                  {get_linkcard_title_and_url(url)[1]}
                </div>
                <a
                  href={get_linkcard_title_and_url(url)[1]}
                  target="_blank"
                  className="absolute top-0 bottom-0 left-0 right-0"
                  rel="noreferrer"
                >
                  {" "}
                </a>
              </div>
            );
          default:
            return (
              <a
                className="text-blue-500"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                {url}
              </a>
            );
        }
      })()}
    </div>
  );
}
